import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, HardDrive, Play, Loader2, Pause, User, Palette, X, Focus, PlaneTakeoff, Home, Trees, Box } from 'lucide-react';
import { customerApi, Collectiv3Scene } from '../services/customerApi';
import { Viewer } from '@ydrive/gs-viewer';
import { SparkViewer, usesSparkRenderer } from '../utils/SparkViewer';
import { modifiers } from '@ydrive/sparky';
import { resolveAssetUrl } from '../utils/assetUrl';
import { useAuth } from '../contexts/AuthContext';
import PageMasthead from '../components/PageMasthead';
import SiteHeader from '../components/SiteHeader';
import FlythroughTool from '../components/FlythroughTool';
import LabelTool from '../components/LabelTool';
import FocusTool from '../components/FocusTool';
import MeasureTool from '../components/MeasureTool';
import EditSceneTool from '../components/EditSceneTool';
import { I18nProvider } from '../i18n';
import * as THREE from 'three';

// Load logo from public folder
const logo = '/logo.gif';

const DHANAS_HOUSE_SCENE_KEYS = new Set(['dhanas_house', 'dhanas house']);
const HOLT_HOMES_SCENE_KEYS = new Set(['holt_homes', 'holt homes', 'holt_homes_jan_2026', 'holt homes jan 2026']);

const isDhanasHouseScene = (sceneNameOrId: string | undefined): boolean => {
  if (!sceneNameOrId) return false;
  return DHANAS_HOUSE_SCENE_KEYS.has(sceneNameOrId.toLowerCase());
};

const isHoltHomesScene = (sceneNameOrId: string | undefined): boolean => {
  if (!sceneNameOrId) return false;
  return HOLT_HOMES_SCENE_KEYS.has(sceneNameOrId.toLowerCase());
};

const normalizeMarketingScene = (scene: Collectiv3Scene): Collectiv3Scene => {
  if (!isDhanasHouseScene(scene.sceneName) && !isDhanasHouseScene(scene.sceneId)) {
    return scene;
  }

  return {
    ...scene,
    sceneName: 'Dhanas House',
    location: scene.location || 'Dhanas',
    cardDescription: scene.cardDescription || 'A residential house reconstruction captured as an interactive Gaussian splat scene.',
    loadingDescription: scene.loadingDescription || 'Loading the Dhanas House 3D reconstruction...',
    sceneDescription: scene.sceneDescription || 'Interactive 3D reconstruction of Dhanas House.',
    credits: scene.credits || 'Cr3ate',
    shootTime: scene.shootTime || 'N/A',
  };
};

const validCameraTuple = (value: unknown): [number, number, number] | undefined => {
  if (!Array.isArray(value) || value.length !== 3 || !value.every(Number.isFinite)) return undefined;
  return value as [number, number, number];
};

// Function to get camera position based on scene name
    const getCameraPositionForScene = (sceneName: string): [number, number, number] | undefined => {
      switch (sceneName.toLowerCase()) {
        case 'dhanas_house':
        case 'dhanas house':
          return [-1.50, 0.41, -10.32];
        case 'vexcel-14':
          return  [1.92,61.96,-45.77];
        case 'vexcel-18':
          return [1.92,61.96,-45.77];
        case 'via-boxed':
          return [18.02,1.9,6.25];
        case 'via-complete':
          return [18.02,1.9,6.25];
        case 'apartment':
          return [-1.237, -0.035, 1.599];
        case 'allisonroad':
          return [1638.017, 92.323, -3018.730];
        case 'wingwalker':
	          return [-38.32, 37.13, 14.82];
        case 'holt_homes':
        case 'holt homes':
        case 'holt_homes_jan_2026':
        case 'holt homes jan 2026':
          return [-944.59, 275.04, 844.26];
        case 'drone_construction_site':
          return [-944.59, 275.04, 844.26];
        case 'home_construction':
          return [-1.50, 0.41, -10.32];
        case 'city_segment':
          return [-1715.1094287552594, 58.061667415452334, 822.1945422993398];
	        case 'prairie':
            return [-13.64, 11.52, -16.29];
	        case 'prairie-1':
	          return [-13.64, 11.52, -16.29];
	        case 'prairie-2':
	          return [-2.76, 11.52, -7.31];
        case 'mosaic':
            return [26.08, -0.04, 7.17];
        case 'spexi':
          return [-83.06, 100.92, -30.26];
        case 'montreal_2':
        case 'montreal-2':
          return [-2352.858, 23.012, 487.973];
        case 'amsterdam':
          return [-850.206, 74.577, 1719.722];
        case 'tallin':
          return [-515.655, 77.214, 362.353];
        case 'tallin_july':
          return [-658.71, 82.66, 431.74];
        // Lafayette's two records both point at full_lafayette_culled.rad,
        // whose geometry sits near (265, 0, 693). The values here used to be
        // ~(-2750, 230, 335), tuned for vexcel-lafayette.rad — a different
        // reconstruction with a different origin. Because .rad loads on
        // demand, a camera pointed at empty space requests nothing after the
        // first few chunks: no error, no progress, black screen. If a scene
        // here ever goes black, check the asset was not swapped underneath
        // these coordinates before looking at the loader.
        case 'lafayette':
          return [265, 450, 1200];
        case 'tallin_twin':
          return [116.967, 93.125, 170.642];
        case 'amsterdam_center':
          return [-2296.02, 40.35, 2099.95];
        case 'amsterdam_2024_no_hg':
          return [-853.155, 55.612, 1766.843];
        case 'lafayette_2025_east':
          return [212.054, 277.061, 882.716];
        case 'substation':
          return [-312.626, 95.713, 1994.007];
        case 'film academy amsterdam':
          return [-878.809, 58.071, 1894.996];
        case 'amsterdam_square':
          return [-878.809, 58.071, 1894.996];
        case 'city of tallin':
          return [-690.171, 78.263, 386.247];
        case 'house under construction':
          return [-3.549, 0.011, 2.397];
        case 'terrebonne streets':
          return [-1685.73, 57.54, 840.11];
        case 'city of lafayette':
          return [265, 450, 1200];
        case 'construction site aerial view':
          return [50.005, 41.921, -7.230];
        case 'amsterdam residential':
          return [-2278.65, 53.03, 2166.85];
        case 'warriors way, san francisco':
        case 'warriors_way_sf':
          return [-2398.85, 251.16, 4070.53];
        case 'warrior way sf squared':
        case 'warrior_way_sf_squared':
          return [-2496.3, 215.6, 3849.7];
        default:
          return undefined;
      }
    };

    const getCameraLookAtForScene = (sceneName: string): [number, number, number] | undefined => {
      switch (sceneName.toLowerCase()) {
        case 'dhanas_house':
        case 'dhanas house':
          return [-1.50, 0.41, 0];
        case 'vexcel-14':
          return [-2.9,56.15,-39.21]; // Use default lookAt
        case 'vexcel-18':
          return [50.56, 53.56, -18.09]; // Use default lookAt
        case 'via-boxed':
          return [8.09,1.25,5.34];
		        case 'via-complete':
		          return [8.09,1.25,5.34];
        case 'apartment':
          return [-5.720, -1.163, -0.222];
        case 'allisonroad':
          return [1633.610, 92.323, 2999.226];
        case 'holt_homes':
        case 'holt homes':
        case 'holt_homes_jan_2026':
        case 'holt homes jan 2026':
          return [-1030.2868, 227.3243, 777.0526];
        case 'drone_construction_site':
          return [-1030.2868, 227.3243, 777.0526];
        case 'home_construction':
          return [-1.50, 0.41, 0];
        case 'city_segment':
          return [-6640, 34.3, 5200];
		    case 'prairie':
          return [-8.2, 4.44, -11.8];
	        case 'prairie-1':
            return [-8.2, 4.44, -11.8];
	        case 'prairie-2':
	          return [-8.2, 4.44, -11.8];
        case 'mosaic':
          return [16.55, -1.69, 4.62];
        case 'spexi':
          return [-84.45, 91.02, -30.52];
        case 'montreal_2':
        case 'montreal-2':
          return [-2352.858, 23.012, 464.527];
        case 'amsterdam':
          return [-842.798, 12.653, 1802.195];
        case 'tallin':
          return [-545.728, 24.764, 406.091];
        case 'tallin_july':
          return [-671.68, 22.07, 345.98];
        case 'lafayette':
          return [265, 60, 693];
        case 'tallin_twin':
          return [71.029, 54.572, 171.616];
        case 'amsterdam_center':
          return [-2297.09, 10.06, 2061.47];
        case 'amsterdam_2024_no_hg':
          return [-879.771, 23.355, 1725.765];
        case 'lafayette_2025_east':
          return [151.074, 224.692, 826.972];
        case 'substation':
          return [-297.591, 95.713, 1982.080];
        case 'film academy amsterdam':
          return [-882.277, 30.083, 1873.707];
        case 'amsterdam_square':
          return [-882.277, 30.083, 1873.707];
        case 'city of tallin':
          return [-708.459, 10.862, 418.755];
        case 'house under construction':
          return [-8.128, 0.011, 0.244];
        case 'terrebonne streets':
          return [-1651.97, 57.54, 852.79];
        case 'city of lafayette':
          return [265, 60, 693];
        case 'construction site aerial view':
          return [50.499, 6.551, -61.738];
        case 'amsterdam residential':
          return [-2296.01, -5.91, 2239.25];
        case 'warriors way, san francisco':
        case 'warriors_way_sf':
          return [-2267.23, 45.52, 3870.58];
        case 'warrior way sf squared':
        case 'warrior_way_sf_squared':
          return [-2374.6, 60.2, 3769.1];
        default:
          return undefined;
      }
    };

    const getLoadingVideoForScene = (sceneName: string): string => {
      switch (sceneName.toLowerCase()) {
        case 'vexcel-14':
          return '';
        case 'vexcel-18':
          return '';
        case 'via-boxed':
          return '';
        case 'via-complete':
          return '';
        case 'mosaic':
          return '';
        default:
          return '';
      }
    };

    const supportsAerialFlythrough = (scene: Collectiv3Scene | undefined): boolean => {
      if (!scene) return false;

	      const name = scene.sceneName.toLowerCase();
		      return ['wingwalker', 'holt_homes', 'holt homes', 'holt_homes_jan_2026', 'holt homes jan 2026', 'drone_construction_site', 'vexcel-14', 'vexcel-18', 'prairie', 'prairie-1', 'prairie-2', 'allisonroad', 'mosaic', 'spexi', 'amsterdam_square'].includes(name);
	    };

    const supportsLabelTool = (scene: Collectiv3Scene | undefined): boolean => {
      return scene?.sceneName.toLowerCase() === 'apartment';
    };

    const supportsMeasurementTool = (scene: Collectiv3Scene | undefined): boolean => {
      const name = scene?.sceneName.toLowerCase() ?? '';
      const id = scene?.sceneId?.toLowerCase() ?? '';
      return (
        isDhanasHouseScene(name) ||
        isDhanasHouseScene(id) ||
        name === 'wingwalker' ||
        name === 'home_construction' ||
        name === 'drone_construction_site' ||
        name === 'city_segment' ||
        isHoltHomesScene(name) ||
        isHoltHomesScene(id)
      );
    };

    const supportsFocusTool = (scene: Collectiv3Scene | undefined): boolean => {
      const name = scene?.sceneName.toLowerCase() ?? '';
      const id = scene?.sceneId?.toLowerCase() ?? '';
      return name === 'wingwalker' || name === 'drone_construction_site' || name === 'spexi' || isHoltHomesScene(name) || isHoltHomesScene(id);
    };

    const supportsPlaceObjectTool = (scene: Collectiv3Scene | undefined): boolean => { // kept for toolbar guard
      return scene?.sceneName.toLowerCase() === 'city_segment';
    };

const MarketingGalleryContent: React.FC = () => {
  const navigate = useNavigate();
  const { authenticated } = useAuth();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [scenes, setScenes] = useState<Collectiv3Scene[]>([]);
  const [showNavPanel, setShowNavPanel] = useState(false);
  const [flythroughActive, setFlythroughActive] = useState(false);
  const [labelToolActive, setLabelToolActive] = useState(false);
  const [focusActive, setFocusActive] = useState(false);
  const [measureActive, setMeasureActive] = useState(false);
  const [placeObjectActive, setPlaceObjectActive] = useState(false);

  // ── Render quality settings ───────────────────────────────────────────────
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [renderBudgetM, setRenderBudgetM] = useState(20);
  const [gradeExposure, setGradeExposure] = useState(0);
  const [gradeSaturation, setGradeSaturation] = useState(1);
  const gradeRef = React.useRef(new modifiers.ColorGrade());

  // ===== 3D VIEWER STATE =====
  const [currentView, setCurrentView] = useState<'gallery' | '3d-viewer'>('gallery');
  const [selectedScene, setSelectedScene] = useState<Collectiv3Scene | undefined>(undefined);
  const [modelLoading, setModelLoading] = useState(false);
  const [loadingProgress, setLoadingProgress] = useState(0);
  const modelContainerRef = React.useRef<HTMLDivElement>(null);
  const progressTextRef = React.useRef<HTMLParagraphElement>(null);
  const viewerRef = React.useRef<any>(null);
  const flythroughBtnRef = React.useRef<HTMLButtonElement>(null);
  const labelToolBtnRef = React.useRef<HTMLButtonElement>(null);
  const focusBtnRef = React.useRef<HTMLButtonElement>(null);
  const measureBtnRef = React.useRef<HTMLButtonElement>(null);
  const placeObjectBtnRef = React.useRef<HTMLButtonElement>(null);
  
  const cameraTrackingRef = React.useRef<number | null>(null);

  // ===== AUDIO STATE =====
  // Fetch all scenes
  const fetchScenes = async () => {
    try {
      setLoading(true);
      setError(null);

      console.log('[MarketingGallery] Fetching all scenes...');
      const response = await customerApi.getAllCollectiv3Scenes();
      
      if (response.success) {
        // Filter to only show scenes with "Marketing" or "Showcase" category
        const filteredScenes = response.data
          .filter(scene => {
            const cat = scene.category?.toLowerCase();
            return cat === 'marketing' || cat === 'showcase' || cat === 'outdoor' || cat === 'indoor' || isDhanasHouseScene(scene.sceneName) || isDhanasHouseScene(scene.sceneId);
          })
          .map(normalizeMarketingScene);
        
        setScenes(filteredScenes);
        console.log(`[MarketingGallery] Loaded ${filteredScenes.length} marketing scenes`);
        
        // Debug: Log thumbnail info for each scene
        filteredScenes.forEach((scene, index) => {
          console.log(`[MarketingGallery] Scene ${index + 1}: ${scene.sceneName}`);
          console.log(`  - Thumbnail: ${scene.thumbnail || 'NOT SET'}`);
          console.log(`  - Category: ${scene.category}`);
        });
      } else {
        throw new Error('Failed to fetch gallery scenes');
      }
    } catch (err) {
      console.error('[MarketingGallery] Error fetching scenes:', err);
      setError(err instanceof Error ? err.message : 'Failed to load gallery scenes');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchScenes();
  }, []);

  // Get category icon
  const getCategoryIcon = (category: string) => {
    switch (category.toLowerCase()) {
      case 'art':
        return <Palette className="w-3 h-3" />;
      case 'people':
        return <User className="w-3 h-3" />;
      default:
        return <HardDrive className="w-3 h-3" />;
    }
  };

  // Get category color
  const getCategoryColor = (category: string) => {
    switch (category.toLowerCase()) {
      case 'art':
        return 'bg-purple-500/20 text-purple-300 border-purple-500/30';
      case 'outdoor':
        return 'bg-green-500/20 text-green-300 border-green-500/30';
      case 'indoor':
        return 'bg-blue-500/20 text-blue-300 border-blue-500/30';
      case 'people':
        return 'bg-orange-500/20 text-orange-300 border-orange-500/30';
      default:
        return 'bg-[#0CB4E4]/20 text-[#0CB4E4] border-[#0CB4E4]/30';
    }
  };

  // Resolves the effective badge type: sceneType takes priority, then category fallback.
  // 'outdoor' in DB maps to 'street' for display purposes.
  const resolveDisplayType = (scene: { sceneType?: string; category?: string }): string | null => {
    const st = scene.sceneType?.toLowerCase();
    if (st === 'outdoor') return 'street';
    if (st) return st;
    const cat = scene.category?.toLowerCase();
    if (cat === 'outdoor') return 'street';
    if (cat === 'indoor') return 'indoor';
    if (cat === 'aerial') return 'aerial';
    return null;
  };

  const getSceneTypeLabel = (type?: string | null) => {
    switch (type) {
      case 'aerial':  return 'Aerial';
      case 'street':  return 'Outdoor';
      case 'indoor':  return 'Indoor';
      case 'object':  return 'Object';
      default:        return null;
    }
  };

  const getSceneTypeIcon = (type?: string | null) => {
    switch (type) {
      case 'aerial':  return <PlaneTakeoff className="w-3 h-3" />;
      case 'street':  return <Trees className="w-3 h-3" />;
      case 'indoor':  return <Home className="w-3 h-3" />;
      case 'object':  return <Box className="w-3 h-3" />;
      default:        return null;
    }
  };

  const getSceneTypeColor = (type?: string | null) => {
    switch (type) {
      case 'aerial':  return 'bg-sky-500/20 text-sky-300 border-sky-500/30';
      case 'street':  return 'bg-green-500/20 text-green-300 border-green-500/30';
      case 'indoor':  return 'bg-blue-500/20 text-blue-300 border-blue-500/30';
      case 'object':  return 'bg-purple-500/20 text-purple-300 border-purple-500/30';
      default:        return 'bg-[#0CB4E4]/20 text-[#0CB4E4] border-[#0CB4E4]/30';
    }
  };

const logCurrentCameraPosition = () => {
  console.log('🔍 === DEBUGGING CAMERA ACCESS ===');
  
  if (!viewerRef.current) {
    console.error('❌ viewerRef.current is null');
    return;
  }
  
  console.log('✅ Viewer exists:', viewerRef.current);
  console.log('🔍 Viewer type:', typeof viewerRef.current);
  console.log('🔍 Viewer keys:', Object.keys(viewerRef.current));
  
  try {
    const viewer = viewerRef.current as any;
    
    // Try different possible property names
    console.log('🔍 Trying viewer.camera:', viewer.camera);
    console.log('🔍 Trying viewer.controls:', viewer.controls);
    console.log('🔍 Trying viewer.splatMesh:', viewer.splatMesh);
    console.log('🔍 Trying viewer.renderer:', viewer.renderer);
    
    // If camera exists
    if (viewer.camera) {
      console.log('✅ Camera found!');
      console.log('🔍 Camera type:', viewer.camera.type);
      console.log('🔍 Camera position:', viewer.camera.position);
      
      if (viewer.camera.position) {
        const pos = viewer.camera.position;
        console.log(`📍 Camera Position: [${pos.x.toFixed(2)}, ${pos.y.toFixed(2)}, ${pos.z.toFixed(2)}]`);
      }
    } else {
      console.error('❌ No camera property found');
    }
    
    // If controls exist
    if (viewer.controls) {
      console.log('✅ Controls found!');
      console.log('🔍 Controls type:', viewer.controls.constructor.name);
      console.log('🔍 Controls target:', viewer.controls.target);
      
      if (viewer.controls.target) {
        const target = viewer.controls.target;
        console.log(`👁️ LookAt Target: [${target.x.toFixed(2)}, ${target.y.toFixed(2)}, ${target.z.toFixed(2)}]`);
      }
    } else {
      console.error('❌ No controls property found');
    }
    
    // Try to get both and format nicely
    if (viewer.camera?.position && viewer.controls?.target) {
      const pos = viewer.camera.position;
      const target = viewer.controls.target;
      
      console.log('\n✨ ========== CURRENT COORDINATES ==========');
      console.log(`Position: [${pos.x.toFixed(2)}, ${pos.y.toFixed(2)}, ${pos.z.toFixed(2)}]`);
      console.log(`LookAt:   [${target.x.toFixed(2)}, ${target.y.toFixed(2)}, ${target.z.toFixed(2)}]`);
      console.log('==========================================\n');
      
      // Also create copyable code
      console.log('📋 Copy this to your code:');
      console.log(`position: [${pos.x.toFixed(2)}, ${pos.y.toFixed(2)}, ${pos.z.toFixed(2)}]`);
      console.log(`lookAt: [${target.x.toFixed(2)}, ${target.y.toFixed(2)}, ${target.z.toFixed(2)}]`);
    }
    
  } catch (error) {
    console.error('❌ Error accessing viewer:', error);
  }
};


  // Real-time camera tracking - logs every 500ms
const startCameraTracking = () => {
  if (!viewerRef.current) return;
  
  if (cameraTrackingRef.current) {
    cancelAnimationFrame(cameraTrackingRef.current);
  }
  
  let lastLogTime = 0;
  const LOG_INTERVAL = 500; // Log every 500ms
  
  const track = () => {
    if (!viewerRef.current) {
      cameraTrackingRef.current = null;
      return;
    }
    
    const now = Date.now();
    if (now - lastLogTime >= LOG_INTERVAL) {
      try {
        const viewer = viewerRef.current as any;
        const camera = viewer.camera;
        
        if (camera && camera.position) {
          // Get camera position
          const pos = camera.position;
          const position = [
            Math.round(pos.x * 100) / 100,
            Math.round(pos.y * 100) / 100,
            Math.round(pos.z * 100) / 100
          ];
          
          // For FirstPersonControls: calculate lookAt from camera direction
          const direction = new THREE.Vector3();
          camera.getWorldDirection(direction);
          
          // Calculate a point 10 units ahead in the direction camera is facing
          const lookAtPoint = new THREE.Vector3(
            pos.x + direction.x * 10,
            pos.y + direction.y * 10,
            pos.z + direction.z * 10
          );
          
          const lookAt = [
            Math.round(lookAtPoint.x * 100) / 100,
            Math.round(lookAtPoint.y * 100) / 100,
            Math.round(lookAtPoint.z * 100) / 100
          ];
          
          console.log(
            `📍 Camera: [${position.join(', ')}] | 👁️ LookAt: [${lookAt.join(', ')}]`
          );
        }
      } catch (error) {
        console.error('❌ Camera tracking error:', error);
      }
      
      lastLogTime = now;
    }
    
    // Continue tracking
    cameraTrackingRef.current = requestAnimationFrame(track);
  };
  
  // Start tracking
  cameraTrackingRef.current = requestAnimationFrame(track);
  console.log('📹 Camera tracking started - watching coordinates');
};


const stopCameraTracking = () => {
  if (cameraTrackingRef.current) {
    cancelAnimationFrame(cameraTrackingRef.current);
    cameraTrackingRef.current = null;
    console.log('📹 Camera tracking stopped');
  }
};

  
  // ===== 3D VIEWER LOGIC =====
  const handleSceneClick = async (scene: Collectiv3Scene) => {
    try {
    console.log(`[MarketingGallery] Loading 3D viewer for scene: ${scene.sceneName}`);
    
    // STEP 1: Cleanup any existing viewer
    if (viewerRef.current && viewerRef.current !== 'cleaning') {
      try {
        await viewerRef.current.dispose();
      } catch (error) {
        console.warn('[MarketingGallery] Error disposing previous viewer:', error);
      }
      viewerRef.current = null;
    }
    
    // STEP 2: Set initial state
    setCurrentView('3d-viewer');
    setSelectedScene(scene);
    setFlythroughActive(false);
    setLabelToolActive(false);
    setFocusActive(false);
    setMeasureActive(false);
    setPlaceObjectActive(false);
    setSettingsOpen(false);
    setRenderBudgetM(20);
    setGradeExposure(0);
    setGradeSaturation(1);
    setModelLoading(true);
    setLoadingProgress(0);
    
    // STEP 3: Basic DOM yield
    await new Promise(resolve => setTimeout(resolve, 100));
    
    // STEP 4: Setup loading text
    if (progressTextRef.current) {
      const fullText = scene.loadingDescription || `Loading ${scene.sceneName}...`;
      const sentences = fullText
        .split(/(?<=[.!?])\s+/)
        .filter(sentence => sentence.trim().length > 0)
        .map(sentence => sentence.trim());
      progressTextRef.current.textContent = sentences.join('\n');
    }
    
    // STEP 5: Container check
    if (!modelContainerRef.current) {
      throw new Error('3D viewer container not found in DOM');
    }
    
  

    // STEP 6: Create viewer
    // DB values are authoritative, allowing production camera changes without
    // a frontend rebuild. The lookup table remains as a backwards-compatible fallback.
    const cameraPosition = validCameraTuple(scene.defaultCameraPos)
      ?? getCameraPositionForScene(scene.sceneName);
    const cameraLookAt = validCameraTuple(scene.defaultLookAt)
      ?? getCameraLookAtForScene(scene.sceneName);
    console.log(`[MarketingGallery] Using camera position:`, cameraPosition);
    console.log(`[MarketingGallery] Using camera lookAt:`, cameraLookAt);

    const viewerOptions: any = {
      sharedMemoryForWorkers: false,
      rootElement: modelContainerRef.current,
      ...(cameraPosition ? { initialCameraPosition: cameraPosition } : {}),
      enableOptimizations: true,
    };

    // Only add initialCameraLookAt if we have a custom one
    if (cameraLookAt) {
      viewerOptions.initialCameraLookAt = cameraLookAt;
   }

    // Single-scene formats (.gaus/.gm/.ply/.rad) render via Spark, same split
    // as SceneViewer. gs-viewer remains the fallback for anything else.
    const viewer: any = usesSparkRenderer(scene.gaus)
      ? new SparkViewer({
          rootElement: modelContainerRef.current,
          ...(cameraPosition ? { initialCameraPosition: cameraPosition } : {}),
          ...(cameraLookAt ? { initialCameraLookAt: cameraLookAt } : {}),
          ...(scene.gaus?.split('?')[0].toLowerCase().endsWith('.rad') ? { cameraFar: 20000 } : {}),
        })
      : new Viewer(viewerOptions);
    viewerRef.current = viewer;
    
    // STEP 7: progress tracking — show raw download percentage only
    const splatBufferOptions = {
      showLoadingUI: false,
      onProgress: (progress: number) => {
        setLoadingProgress(Math.max(0, Math.min(100, Math.round(progress))));
      },
      ...(scene.envMap && {
        environmentMapPath: resolveAssetUrl(scene.envMap),
        deferEnvironmentMap: true,
      }),
      ...(scene.imagePoses && {
        imagePosesPath: resolveAssetUrl(scene.imagePoses),
        deferImagePoses: true,
      }),
      ...((scene as any).coordinateFrame && {
        coordinateFrame: (scene as any).coordinateFrame,
      }),
    };

    // STEP 8: Load scene
    console.log('[MarketingGallery] Starting scene loading...');
    await viewer.addSplatScene(resolveAssetUrl(scene.gaus)!, splatBufferOptions);

    // STEP 9: Start viewer and clear loading
    viewer.start();
    if (usesSparkRenderer(scene.gaus) && scene.sceneType === 'aerial' && viewer.controls) {
      viewer.controls.movementSpeed = 50;
    }
    if (usesSparkRenderer(scene.gaus)) {
      viewer.setLodSplatCount?.(20 * 1_000_000);
      const mesh = viewer.splatMesh;
      if (mesh) {
        gradeRef.current.attach(mesh);
        const isRad = !!scene.gaus?.split('?')[0].toLowerCase().endsWith('.rad');
        if (isRad) {
          const isTerrebonne = scene.sceneName?.toLowerCase() === 'terrebonne streets';
          const exposure = isTerrebonne ? 0.50 : 0;
          setGradeExposure(exposure);
          setGradeSaturation(1.35);
          gradeRef.current.set(mesh, { exposure, saturation: 1.35 });
        }
      }
    }
    setLoadingProgress(100);
    setModelLoading(false);

    (window as any).debugViewer = viewer;
    // Show navigation panel after scene settles
    setTimeout(() => {
      setShowNavPanel(true);
      console.log('[MarketingGallery] Navigation panel shown');
    }, 2000);
    //Start camera tracking
      setTimeout(() => {
        startCameraTracking();
      }, 1000); // Wait 1 second for scene to settle
    
    console.log(`[MarketingGallery] Scene loaded: ${scene.sceneName}`);
    
  } catch (error) {
    console.error('[MarketingGallery] Loading error:', error);
    setModelLoading(false);
    setSelectedScene(undefined);
    setCurrentView('gallery');
    
    const errorMessage = error instanceof Error ? error.message : 'Unknown error';
    alert(`Failed to load 3D viewer: ${errorMessage}`);
  }
};


  // Close viewer function
  const close3DViewer = async () => {
    console.log(`[MarketingGallery] Closing 3D viewer`);

    // Hide navigation panel
    setShowNavPanel(false);
    setFlythroughActive(false);
    setLabelToolActive(false);
    setFocusActive(false);
    setMeasureActive(false);
    setPlaceObjectActive(false);
    // Stop camera tracking
      stopCameraTracking();
    
    // Clean up any running progress intervals
    if ((window as any).progressInterval) {
      clearInterval((window as any).progressInterval);
      (window as any).progressInterval = null;
    }
    
    // Dispose viewer properly
    if (viewerRef.current && viewerRef.current !== 'cleaning') {
      try {
        const viewer = viewerRef.current;
        viewerRef.current = 'cleaning' as any;
        
        if (typeof viewer.dispose === 'function') {
          await viewer.dispose();
        }
        console.log('Viewer disposed successfully');
      } catch (error) {
        console.warn('[MarketingGallery] Error disposing viewer:', error);
      }
      viewerRef.current = null;
    }
    
    // Reset all viewer state
    setSelectedScene(undefined);
    setModelLoading(false);
    setCurrentView('gallery');
    setLoadingProgress(0);

    
    // Clear any loading text
    if (progressTextRef.current) {
      progressTextRef.current.textContent = '';
    }
  };

  // Handle escape key
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (currentView !== '3d-viewer') return;
      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
        close3DViewer();
        return;
      }
      if (event.key === 'h' || event.key === 'H') {
        const target = event.target as Element;
        if (target.closest('input, textarea, [contenteditable]')) return;
        (viewerRef.current as any)?.resetCamera?.();
      }
    };

    if (currentView === '3d-viewer') {
      window.addEventListener('keydown', handleKeyDown);
      return () => window.removeEventListener('keydown', handleKeyDown);
    }
  }, [currentView]);

  // Component cleanup
  useEffect(() => {
    return () => {
      // Stop camera tracking
    stopCameraTracking();
      
      // Cleanup 3D viewer
      if (viewerRef.current && viewerRef.current !== 'cleaning') {
        try {
          if (typeof viewerRef.current.dispose === 'function') {
            viewerRef.current.dispose();
          }
        } catch (error) {
          console.warn('Unmount cleanup error (ignoring):', error);
        } finally {
          viewerRef.current = null;
        }
      }
    };
  }, []);


  // ===== RENDER PUBLIC HEADER (for non-authenticated users) =====
  /*
   * The shared header, as on the other four public pages. This page is where
   * the nav's "Gallery" link lands, and it used to arrive at a bespoke bar with
   * no nav on it at all — so the only way back to Desktop, Reco or Spatial
   * Models was the logo. TheCollectiv3 was already on SiteHeader; this was the
   * one page still carrying its own.
   *
   * What the old bar had and this does not: "Sign In" and "Join the waitlist".
   * SiteHeader has no slot for either.
   */
  const renderPublicHeader = () => (
    <SiteHeader />
  );

  // ===== RENDER GALLERY VIEW =====
  const renderGalleryView = () => (
    <div className="min-h-screen bg-[#161513]">
      {/* Conditional Header - Show public header if not authenticated, Layout will handle if authenticated */}
      {!authenticated && renderPublicHeader()}
      {/* Header */}
      <div className="relative bg-[#161513] border-b border-[#0CB4E4]/20">
        {/* Logo Background */}
        <div 
          className="absolute top-0 left-0 right-0 h-full bg-cover bg-center bg-no-repeat opacity-10 z-0 pointer-events-none"
          style={{ backgroundImage: `url(${logo})` }}
        />
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 relative z-10">
        {/* Back button - only show for authenticated users */}
        {authenticated && (
          <div className="flex items-center justify-between mb-4">
            <button 
              onClick={() => navigate(-1)} 
              className="flex items-center gap-2 text-gray-300 hover:text-white transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
              Back
            </button>
          </div>
        )}

        {/* Left-aligned and at the shared size, so the five public pages read
            as one site. It was centred at text-6xl bold, which put a third of a
            laptop screen between the header and the first scene card. Titled
            "Gallery" to match the nav link that gets you here — "Marketing
            Gallery" named an internal collection, not a destination. */}
        <PageMasthead
          title="Gallery"
          tagline="A selection of 3D reconstructions made using the Cr3ate pipeline"
        />
      </div>
    </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-12">
        {/* Loading State */}
        {loading && (
          <div className="text-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-[#0CB4E4] mx-auto mb-4" />
            <h3 className="text-lg font-medium text-white mb-2">Loading Gallery...</h3>
            <p className="text-gray-300">Preparing our 3D collection for you</p>
          </div>
        )}

        {/* Error State */}
        {error && (
          <div className="bg-red-900/20 text-red-400 p-6 rounded-xl border border-red-500/30 mb-8">
            <h3 className="font-medium mb-2">Unable to Load Gallery</h3>
            <p>{error}</p>
            <button 
              onClick={fetchScenes}
              className="mt-4 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors"
            >
              Try Again
            </button>
          </div>
        )}

        {/* All Scenes Grid */}
        {!loading && !error && scenes.length > 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {scenes.map((scene) => (
              <div
                key={`${scene.sceneName}-${scene.category}`}
                onClick={() => handleSceneClick(scene)}
                className="relative overflow-hidden h-[480px] bg-[#161513] shadow-lg border border-[#0CB4E4]/30 hover:shadow-xl transition-all duration-300 hover:scale-[1.02] rounded-xl group cursor-pointer"
              >
                {/* Scene Preview */}
                <div className="absolute inset-0 w-full h-full bg-[#161513] overflow-hidden rounded-xl">
                  <div className="h-1/2 bg-gradient-to-br from-[#0CB4E4]/20 via-[#0CB4E4]/10 to-[#0CB4E4]/20 relative flex items-center justify-center">
                    {/* Thumbnail or Logo */}
                    {scene.thumbnail ? (
                      <img 
                        src={scene.thumbnail} 
                        alt={scene.sceneName}
                        className="w-full h-full object-cover transition-transform duration-500 ease-out group-hover:scale-110"
                        onError={(e) => {
                          // Fallback to logo if thumbnail fails to load
                          console.error(`[MarketingGallery] Thumbnail failed to load: ${scene.thumbnail} for scene: ${scene.sceneName}`);
                          (e.target as HTMLImageElement).src = logo;
                          (e.target as HTMLImageElement).className = "h-24 object-contain transition-transform duration-500 ease-out group-hover:scale-110 opacity-60";
                        }}
                        onLoad={() => {
                          console.log(`[MarketingGallery] Thumbnail loaded successfully: ${scene.thumbnail} for scene: ${scene.sceneName}`);
                        }}
                      />
                    ) : (
                      <>
                        {console.log(`[MarketingGallery] No thumbnail found for scene: ${scene.sceneName}, using logo fallback`)}
                        <img 
                          src={logo} 
                          alt="3D Scene Preview" 
                          className="h-24 object-contain transition-transform duration-500 ease-out group-hover:scale-110 opacity-60" 
                        />
                      </>
                    )}
                    
                    {/* Play overlay */}
                    <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                      <div className="w-16 h-16 bg-[#0CB4E4] rounded-full flex items-center justify-center">
                        <Play className="w-8 h-8 text-[#161513] ml-1" />
                      </div>
                    </div>

                    {/* Scene type badge */}
                    {(() => { const dt = resolveDisplayType(scene); return getSceneTypeLabel(dt) && (
                      <div className={`absolute top-3 right-3 backdrop-blur-sm px-3 py-1 text-xs font-medium border rounded-full flex items-center gap-1 ${getSceneTypeColor(dt)}`}>
                        {getSceneTypeIcon(dt)}
                        <span>{getSceneTypeLabel(dt)}</span>
                      </div>
                    ); })()}
                  </div>

                  {/* Gradient overlay */}
                  <div className="absolute inset-0 bg-gradient-to-t from-[#161513] via-transparent to-transparent" />
                </div>

                {/* Scene Info - Enhanced with cardDescription and credits */}
                <div className="absolute bottom-0 left-0 right-0 p-5 z-10 bg-gradient-to-t from-[#161513] via-[#161513]/95 to-transparent min-h-[240px] flex flex-col justify-end">
                  <h3 className="font-semibold text-white text-base mb-2 line-clamp-1" title={scene.sceneName}>
                    {scene.sceneName}
                  </h3>
                  
                  {/* Card Description */}
                  <p className="text-sm text-gray-300 line-clamp-3 mb-3" title={scene.cardDescription}>
                    {scene.cardDescription}
                  </p>

                  {/* Credits - Show artist tag if category is Art */}
                  {scene.credits && (
                    <div className="mb-3">
                      <span className="text-xs text-gray-400">
                        {scene.category.toLowerCase() === 'art' ? 'Artist: ' : 'Credits: '}
                      </span>
                      <span className="text-xs text-[#0CB4E4] font-medium">
                        {scene.credits}
                      </span>
                    </div>
                  )}

                  {/* Location */}
                  {scene.location && (
                    <div className="mb-3">
                      <span className="text-xs text-gray-400">Location: </span>
                      <span className="text-xs text-gray-300 font-medium">
                        {scene.location}
                      </span>
                    </div>
                  )}

                  {/* Scene Stats */}
                  <div className="flex items-center justify-between text-xs text-gray-400">
                    <div className="flex items-center gap-1">
                      <HardDrive className="w-3 h-3" />
                      <span>3D Scene</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Play className="w-3 h-3" />
                      <span>Interactive</span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Empty State */}
        {!loading && !error && scenes.length === 0 && (
          <div className="text-center py-20">
            <img 
              src={logo} 
              alt="No scenes" 
              className="w-16 h-16 mx-auto mb-4 object-contain opacity-50" 
            />
            <h3 className="text-lg font-medium text-white mb-2">No Scenes Available</h3>
            <p className="text-gray-300">
              The gallery collection is being prepared. Check back soon!
            </p>
          </div>
        )}
      </div>
    </div>
  );

  // ===== RENDER MULTI-PHASE LOADING UI =====
  const loadingVideoSrc = selectedScene ? getLoadingVideoForScene(selectedScene.sceneName) : '';
  const renderLoadingUI = () => (
  <div className="fixed inset-0 z-[1002]">
    {/* Background video */}
    <video
      key="loading-bg-video"
      src={loadingVideoSrc}
      className="absolute inset-0 w-full h-full object-cover"
      autoPlay
      muted
      loop
      playsInline
    />

    {/* Subtle vignette for readability */}
    <div className="absolute inset-0 bg-gradient-to-b from-black/0 via-black/0 to-black/20 pointer-events-none" />

    {/* Positioned, elegant glass panel */}
    <div className="absolute left-1/2 top-1/3 -translate-x-1/2 -translate-y-1/2 w-[32rem] max-w-[92%]">
      {/* Gradient border wrapper */}
      <div className="p-[1.5px] rounded-3xl bg-gradient-to-br from-white/25 via-[#0CB4E4]/40 to-transparent shadow-2xl">
        <aside className="relative rounded-3xl bg-[#0E0D0C]/[0.15] backdrop-blur-xl border border-white/10 overflow-hidden">
          {/* Close Button */}
          <button
            onClick={close3DViewer}
            className="absolute top-4 right-4 z-10 w-8 h-8 grid place-items-center bg-white/10 hover:bg-white/20 rounded-full text-white/60 hover:text-white transition-colors"
            aria-label="Close viewer"
          >
            <span className="text-xl font-light">✕</span>
          </button>
          
          {/* Header */}
          <div className="px-6 pt-5 pb-3 border-b border-white/10">
            <h2 className="text-white text-xl font-semibold tracking-tight truncate pr-8">
              {selectedScene?.sceneName || 'Loading Scene'}
            </h2>
          </div>

          {/* Body */}
          <div className="px-6 py-4 space-y-5">
            {/* Description (scrollable if long) */}
            <div className="max-h-40 overflow-y-auto pr-1">
              <p
                ref={progressTextRef}
                className="text-white/90 text-[0.95rem] leading-6 whitespace-pre-wrap break-words"
                style={{
                  fontFamily: "'Source Sans Pro', 'Helvetica Neue', Arial, sans-serif",
                }}
              >
                {selectedScene?.loadingDescription}
              </p>
            </div>

            {/* Progress */}
            <div className="space-y-3">
              {/* Bar with inline % */}
              <div className="relative w-full h-3.5 rounded-full bg-white/10 overflow-hidden">
                <div
                  className="absolute inset-y-0 left-0 bg-gradient-to-r from-[#0CB4E4] to-[#0CB4E4]/85 transition-all duration-300 ease-out"
                  style={{ width: `${loadingProgress}%` }}
                />
                <div className="absolute inset-0 grid place-items-center">
                  <span className="text-xs font-medium text-white/90 drop-shadow">
                    {loadingProgress}% Complete
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Footer tagline */}
          <div className="px-6 py-4 border-t border-white/10 text-center">
            <p className="text-white/95 text-sm tracking-wide">
              Experience it like you are there!
            </p>
          </div>
        </aside>
      </div>
    </div>
  </div>
);

  // ===== RENDER 3D VIEWER =====
  const render3DViewer = () => (
    <div className="fixed inset-0 w-full h-full bg-black z-[999]">
      {/* Top-right: settings panel + vertical icon strip + close */}
      {(() => {
        const isRadScene = !!(selectedScene?.gaus && selectedScene.gaus.split('?')[0].toLowerCase().endsWith('.rad'));
        const isAerialScene = selectedScene?.sceneType === 'aerial';
        return (
          <div className="fixed top-4 right-4 z-[1001] flex items-start gap-3">

            {/* Settings panel — pops to the left of the strip */}
            {settingsOpen && isRadScene && (
              <div
                className="w-64 rounded-xl shadow-2xl p-4 text-sm"
                style={{
                  background: 'rgba(10,15,25,0.92)',
                  backdropFilter: 'blur(12px)',
                  border: '1px solid rgba(255,255,255,0.12)',
                  color: 'white',
                }}
              >
                <div className="flex items-center justify-between mb-3">
                  <p className="text-[10px] font-semibold text-white/40 uppercase tracking-wider">Settings</p>
                  <button
                    onClick={() => setSettingsOpen(false)}
                    style={{ color: 'rgba(255,255,255,0.4)', background: 'none', border: 'none', cursor: 'pointer', padding: 4, borderRadius: 6, lineHeight: 1 }}
                    onMouseEnter={e => (e.currentTarget.style.color = 'white')}
                    onMouseLeave={e => (e.currentTarget.style.color = 'rgba(255,255,255,0.4)')}
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                      <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                    </svg>
                  </button>
                </div>
                <p className="text-[10px] font-semibold text-white/40 uppercase tracking-wider mb-3">Render Quality</p>
                <div className="mb-1 flex items-center justify-between">
                  <span className="text-xs text-white/70">Render budget</span>
                  <span className="text-xs font-semibold text-[#FDBA59]">{renderBudgetM.toFixed(0)}M splats</span>
                </div>
                <input
                  type="range" min={1} max={20} step={1} value={renderBudgetM}
                  onChange={e => {
                    const val = Number(e.target.value);
                    setRenderBudgetM(val);
                    (viewerRef.current as SparkViewer | null)?.setLodSplatCount?.(val * 1_000_000);
                  }}
                  className="w-full accent-[#FDBA59]"
                />
                <div className="flex justify-between text-[10px] text-white/30 mt-0.5">
                  <span>1M</span><span>20M</span>
                </div>
                <p className="text-[10px] text-white/30 mt-3 leading-relaxed">
                  Higher values improve detail. Reduce if frame rate drops.
                </p>

                <p className="text-[10px] font-semibold text-white/40 uppercase tracking-wider mt-4 mb-3">Color Grading</p>

                <div className="mb-3">
                  <div className="mb-1 flex items-center justify-between">
                    <span className="text-xs text-white/70">Exposure</span>
                    <span className="text-xs font-semibold text-[#FDBA59]">{gradeExposure >= 0 ? '+' : ''}{gradeExposure.toFixed(2)}</span>
                  </div>
                  <input
                    type="range" min={-2} max={2} step={0.05} value={gradeExposure}
                    onChange={e => {
                      const val = Number(e.target.value);
                      setGradeExposure(val);
                      const mesh = (viewerRef.current as SparkViewer | null)?.splatMesh;
                      if (mesh) gradeRef.current.set(mesh, { exposure: val });
                    }}
                    className="w-full accent-[#FDBA59]"
                  />
                  <div className="flex justify-between text-[10px] text-white/30 mt-0.5">
                    <span>-2</span><span>0</span><span>+2</span>
                  </div>
                </div>

                <div className="mb-3">
                  <div className="mb-1 flex items-center justify-between">
                    <span className="text-xs text-white/70">Saturation</span>
                    <span className="text-xs font-semibold text-[#FDBA59]">{gradeSaturation.toFixed(2)}</span>
                  </div>
                  <input
                    type="range" min={0} max={2} step={0.05} value={gradeSaturation}
                    onChange={e => {
                      const val = Number(e.target.value);
                      setGradeSaturation(val);
                      const mesh = (viewerRef.current as SparkViewer | null)?.splatMesh;
                      if (mesh) gradeRef.current.set(mesh, { saturation: val });
                    }}
                    className="w-full accent-[#FDBA59]"
                  />
                  <div className="flex justify-between text-[10px] text-white/30 mt-0.5">
                    <span>0</span><span>1</span><span>2</span>
                  </div>
                </div>

                <button
                  onClick={() => {
                    setGradeExposure(0);
                    setGradeSaturation(1);
                    const mesh = (viewerRef.current as SparkViewer | null)?.splatMesh;
                    if (mesh) gradeRef.current.set(mesh, { exposure: 0, saturation: 1 });
                  }}
                  className="w-full rounded-md py-1 text-[11px] text-white/40 hover:text-white/70 hover:bg-white/5 transition-colors"
                >
                  Reset grading
                </button>
              </div>
            )}

            {/* Vertical icon strip */}
            <div className="flex flex-col gap-1 rounded-xl bg-zinc-900/80 p-1.5 backdrop-blur-md">

              {/* Settings — .rad scenes only */}
              {isRadScene && (
                <button
                  onClick={() => setSettingsOpen(o => !o)}
                  disabled={modelLoading}
                  title="Render quality &amp; color grading"
                  className={`flex h-10 w-10 items-center justify-center rounded-lg transition-colors disabled:opacity-40 ${settingsOpen ? 'bg-amber-500/20 text-amber-300 ring-1 ring-amber-400/40' : 'text-white/50 hover:bg-white/10 hover:text-white'}`}
                >
                  <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="3" />
                    <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
                  </svg>
                </button>
              )}

              {/* Measure — always */}
              <button
                ref={measureBtnRef}
                onClick={() => {
                  const next = !measureActive;
                  setMeasureActive(next);
                  if (next) {
                    setFlythroughActive(false);
                    setShowNavPanel(false);
                  }
                }}
                disabled={modelLoading}
                title="Measure"
                className={`flex h-10 w-10 items-center justify-center rounded-lg transition-colors disabled:opacity-40 ${measureActive ? 'bg-cyan-500/20 text-cyan-300 ring-1 ring-cyan-400/40' : 'text-white/50 hover:bg-white/10 hover:text-white'}`}
              >
                <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M2 12h20" />
                  <path d="M2 12l3-3M2 12l3 3" />
                  <path d="M22 12l-3-3M22 12l-3 3" />
                  <line x1="8" y1="9" x2="8" y2="15" />
                  <line x1="12" y1="7" x2="12" y2="17" />
                  <line x1="16" y1="9" x2="16" y2="15" />
                </svg>
              </button>

              {/* Flythrough — aerial scenes only */}
              {isAerialScene && (
                <button
                  ref={flythroughBtnRef}
                  onClick={() => {
                    const next = !flythroughActive;
                    setFlythroughActive(next);
                    if (next) {
                      setMeasureActive(false);
                      setShowNavPanel(false);
                    }
                  }}
                  disabled={modelLoading}
                  title="Flythrough"
                  className={`flex h-10 w-10 items-center justify-center rounded-lg transition-colors disabled:opacity-40 ${flythroughActive ? 'bg-sky-500/20 text-sky-300 ring-1 ring-sky-400/40' : 'text-white/50 hover:bg-white/10 hover:text-white'}`}
                >
                  <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21 12a9 9 0 1 1-3.18-6.87" />
                    <path d="M21 3v6h-6" />
                    <circle cx="12" cy="12" r="1.5" />
                  </svg>
                </button>
              )}

              {/* Reset camera */}
              <button
                onClick={() => (viewerRef.current as any)?.resetCamera?.()}
                disabled={modelLoading}
                title="Reset view (H)"
                className="flex h-10 w-10 items-center justify-center rounded-lg text-white/50 hover:bg-white/10 hover:text-white transition-colors disabled:opacity-40"
              >
                <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>
                  <polyline points="9 22 9 12 15 12 15 22"/>
                </svg>
              </button>

              {/* Divider */}
              <div className="mx-2 my-0.5 h-px bg-white/10" />

              {/* Close */}
              <button
                onClick={close3DViewer}
                title="Close (ESC)"
                className="flex h-10 w-10 items-center justify-center rounded-lg text-white/50 hover:bg-white/10 hover:text-white transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

          </div>
        );
      })()}

      {/* Brand overlay */}
      <div className="fixed top-4 left-4 z-[1001] flex items-center gap-2">
        <img src={logo} alt="CR3ATE.AI" className="h-10 w-10 object-contain" />
        <h1 className="font-light text-xl text-[#0CB4E4] font-brand">
          CR3ATE.AI
        </h1>
      </div>

      {/* Multi-phase Loading UI */}
      {modelLoading && renderLoadingUI()}

      {/* 3D Model Viewer Container */}
      <div
        ref={modelContainerRef}
        className="absolute inset-0 w-full h-full"
        style={{
          zIndex: 1000,
          backgroundColor: "black"
        }}
      />

      {!modelLoading && selectedScene?.sceneType === 'aerial' && flythroughActive && (
        <FlythroughTool
          viewerRef={viewerRef}
          isActive={flythroughActive}
          onClose={() => setFlythroughActive(false)}
          anchorRef={flythroughBtnRef}
        />
      )}

      {!modelLoading && supportsFocusTool(selectedScene) && focusActive && (
        <FocusTool
          viewerRef={viewerRef}
          isActive={focusActive}
          onClose={() => setFocusActive(false)}
          anchorRef={focusBtnRef}
        />
      )}

      {!modelLoading && measureActive && (
        <MeasureTool
          viewerRef={viewerRef}
          isActive={measureActive}
          onClose={() => setMeasureActive(false)}
          anchorRef={measureBtnRef}
        />
      )}

      {!modelLoading && selectedScene && supportsLabelTool(selectedScene) && (
        <LabelTool
          sceneId={selectedScene.sceneId}
          viewerRef={viewerRef}
          isActive={labelToolActive}
          onClose={() => setLabelToolActive(false)}
          anchorRef={labelToolBtnRef}
        />
      )}

      {!modelLoading && supportsPlaceObjectTool(selectedScene) && (
        <EditSceneTool
          viewerRef={viewerRef}
          isActive={placeObjectActive}
          onClose={() => setPlaceObjectActive(false)}
          anchorRef={placeObjectBtnRef}
        />
      )}

          {showNavPanel && !modelLoading && (
        <div 
          className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[1002]"
          onClick={() => setShowNavPanel(false)}
          style={{ fontFamily: "'Inter', sans-serif" }}
        >
          <div 
            className="relative w-full max-w-[620px] mx-6 p-8 md:p-10 rounded-[28px] text-white"
            style={{
              background: 'rgba(255, 255, 255, 0.08)',
              backdropFilter: 'blur(4px) saturate(120%)',
              WebkitBackdropFilter: 'blur(4px) saturate(120%)',
              border: '1px solid rgba(255, 255, 255, 0.22)',
              boxShadow: '0 20px 50px rgba(0, 0, 0, 0.4)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setShowNavPanel(false)}
              className="absolute top-5 right-5"
              style={{
                width: '28px',
                height: '28px',
                borderRadius: '50%',
                background: 'rgba(255, 255, 255, 0.08)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                opacity: 0.6,
                transition: 'opacity 0.2s'
              }}
            >
              <X size={12} strokeWidth={3} />
            </button>

            <h1 className="text-2xl text-center mb-10 tracking-widest opacity-90" style={{ fontWeight: 300 }}>
              Navigation
            </h1>

            <div className="flex flex-col md:flex-row items-stretch gap-10">
              <div className="flex-1 flex flex-col items-center">
                <h2 className="text-lg font-normal mb-8 opacity-80 h-8 flex items-center tracking-wide">
                  Look around
                </h2>

                <div className="relative w-40 h-40 flex items-center justify-center mb-8">
                  <div 
                    style={{
                      width: '56px',
                      height: '90px',
                      border: '1.5px solid rgba(255, 255, 255, 0.4)',
                      borderRadius: '26px',
                      position: 'relative',
                      zIndex: 2
                    }}
                  >
                    <div 
                      style={{
                        width: '4px',
                        height: '8px',
                        background: 'white',
                        borderRadius: '1.5px',
                        position: 'absolute',
                        top: '18px',
                        left: '50%',
                        transform: 'translateX(-50%)',
                        opacity: 0.9
                      }}
                    />
                  </div>
                  
                  <div 
                    style={{
                      position: 'absolute',
                      width: '120px',
                      height: '120px',
                      border: '1.2px solid rgba(255, 255, 255, 0.25)',
                      borderLeftColor: 'transparent',
                      borderBottomColor: 'transparent',
                      borderRadius: '50%',
                      top: '50%',
                      left: '50%',
                      transform: 'translate(-50%, -50%) rotate(-25deg)',
                      zIndex: 1
                    }}
                  />
                </div>

                <p className="text-[12px] uppercase tracking-[0.25em] font-light text-white/40">
                  Click & drag
                </p>
              </div>

              <div 
                className="hidden md:block w-px"
                style={{
                  background: 'linear-gradient(to bottom, transparent 0%, rgba(255,255,255,0.2) 50%, transparent 100%)'
                }}
              />

              <div className="flex-1 flex flex-col items-center">
                <h2 className="text-lg font-normal mb-8 opacity-80 h-8 flex items-center tracking-wide">
                  Move
                </h2>

                <div className="relative mb-8" style={{ width: '180px', height: '120px' }}>
                  <div className="grid grid-cols-3 gap-2">
                    <div></div>
                    <div className="w-14 h-14 rounded-lg border border-white/30 bg-white/5 flex items-center justify-center text-xl font-light">
                      W
                    </div>
                    <div></div>
                    <div className="w-14 h-14 rounded-lg border border-white/30 bg-white/5 flex items-center justify-center text-xl font-light">
                      A
                    </div>
                    <div className="w-14 h-14 rounded-lg border border-white/30 bg-white/5 flex items-center justify-center text-xl font-light">
                      S
                    </div>
                    <div className="w-14 h-14 rounded-lg border border-white/30 bg-white/5 flex items-center justify-center text-xl font-light">
                      D
                    </div>
                  </div>
                </div>

                <p className="text-[12px] uppercase tracking-[0.25em] font-light text-white/40">
                  Arrow keys
                </p>
              </div>
            </div>

            <div className="mt-10 text-center">
              <p className="text-sm text-white/60">
                Click anywhere to start exploring
              </p>
            </div>
          </div>
        </div>
      )}

    </div>
  );
  // ===== MAIN RENDER =====
  return (
    <>
      {currentView === 'gallery' && renderGalleryView()}
      {currentView === '3d-viewer' && render3DViewer()}
    </>
  );
};

const MarketingGallery: React.FC = () => (
  <I18nProvider initialLang="en">
    <MarketingGalleryContent />
  </I18nProvider>
);

export default MarketingGallery;
